// src/components/MembershipsPage.js
import React, { useState, useEffect } from "react";
import MembershipProductCard from "./MembershipProductCard";
import SEO from "./SEO";
import { FEATURES } from "../config";
import "./MembershipsPage.css";

const getEffectiveMetadata = (product) => ({
  ...(product.metadata || {}),
  ...(product.price?.metadata || {}),
});

const isUnifiedSharedMembership = (product) => {
  const metadata = getEffectiveMetadata(product);
  return (
    metadata.creditModel === "unified_shared" &&
    (metadata.productType === "membership" ||
      metadata.creditCategory === "membership")
  );
};

const isLegacyMembership = (product) => {
  const metadata = getEffectiveMetadata(product);
  return (
    metadata.productType === "sauna-membership" ||
    metadata.productType === "red-light-membership" ||
    metadata.productType === "combo-membership"
  );
};

const getMembershipSortValue = (product) => {
  const metadata = getEffectiveMetadata(product);
  const rawValue = metadata.sessionCredits || metadata.totalSessions;

  if (rawValue === "unlimited") {
    return Number.MAX_SAFE_INTEGER;
  }

  const numericValue = Number(rawValue);
  if (Number.isFinite(numericValue) && numericValue > 0) {
    return numericValue;
  }

  return Number.MAX_SAFE_INTEGER - 1;
};

const MembershipsPage = () => {
  const [memberships, setMemberships] = useState([]);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const fetchMemberships = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/products`
        );
        const products = await response.json();
        const unifiedMemberships = products.filter(isUnifiedSharedMembership);

        if (FEATURES.ENABLE_UNIFIED_STOREFRONT && unifiedMemberships.length > 0) {
          const sortedUnifiedMemberships = [...unifiedMemberships].sort(
            (a, b) =>
              getMembershipSortValue(a) - getMembershipSortValue(b) ||
              a.name.localeCompare(b.name)
          );
          setMemberships(sortedUnifiedMemberships);
          return;
        }

        const membershipProducts = products.filter(isLegacyMembership);
        const comboMemberships = membershipProducts.filter((product) => {
          const metadata = getEffectiveMetadata(product);
          return metadata.productType === "combo-membership";
        });
        const saunaMemberships = membershipProducts.filter((product) => {
          const metadata = getEffectiveMetadata(product);
          return metadata.productType === "sauna-membership";
        });
        const redLightMemberships = membershipProducts.filter((product) => {
          const metadata = getEffectiveMetadata(product);
          return metadata.productType === "red-light-membership";
        });

        const sortByNameAsc = (a, b) => a.name.localeCompare(b.name);
        comboMemberships.sort(sortByNameAsc);
        saunaMemberships.sort(sortByNameAsc);
        redLightMemberships.sort(sortByNameAsc);

        setMemberships([
          ...comboMemberships,
          ...saunaMemberships,
          ...redLightMemberships,
        ]);
      } catch (error) {
        console.error("Error fetching memberships:", error);
      }
    };

    fetchMemberships();
  }, []);

  return (
    <div className="memberships-page">
      <SEO
        title="Wellness Memberships | Diviti Adora Studio | Oro Valley AZ"
        description="Explore our infrared sauna and red light therapy membership options at Diviti Adora in Oro Valley. Choose from sauna-only, red light-only, or combo memberships for your wellness journey."
        keywords="wellness memberships, infrared sauna membership, red light therapy membership, combo membership, Oro Valley wellness, monthly wellness plans, wellness subscriptions, infrared sauna Oro Valley AZ, red light therapy near Oro Valley, memberships for infrared sauna Oro Valley, packages for red light therapy Tucson, mind body wellness Oro Valley, preventative health solutions Tucson"
        canonicalUrl="/memberships"
      />
      <header className="memberships-header">
        <h1>Memberships</h1>
        <p>
          Explore our membership options designed for ultimate wellness and
          flexible shared access.
        </p>
      </header>
      <section className="memberships-content">
        {memberships.length === 0 ? (
          <p>No memberships available at the moment.</p>
        ) : (
          <div className="membership-cards">
            {memberships.map((product) => (
              <MembershipProductCard
                key={product.id}
                product={product}
                friendlyNames={null} // if you want to pass custom mappings
              />
            ))}
          </div>
        )}
      </section>
      {message && <p className="message">{message}</p>}
    </div>
  );
};

export default MembershipsPage;
